<template>
  <div style="margin:20px">
    <FiPageRail title="Actuals - Driver Daily Report"/>
    <div style="position: relative; font-weight: bold; font-size: 24px" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>&nbsp;&nbsp;
      <a href="#" class="arrows" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;&nbsp;
      <span v-if="driver_tags_options">
        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
        Tags:&nbsp;<b-form-select v-model="selected_driver_tag" :options="driver_tags_options" style="font-size: 18px; font-weight: bold; width:220px"/>
      </span>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_data(false)" :disabled="loading">Get Results</b-button>
    </div>
    <div v-if="results_cached_at" class="text-center mt-1" style="font-size: 14px; color: #888;">
      Using data from {{ formattedCachedAt }}
      — <a href="#" @click.prevent="load_data(true)" style="font-size: 14px;">Pull New Data</a>
    </div>

    <div v-if="polling" class="mt-3 p-3 border rounded bg-light d-flex align-items-center justify-content-center">
      <b-spinner style="width: 3rem; height: 3rem;" class="mr-3" label="Loading..."></b-spinner>
      <span>
        <template v-if="!email_opt_in_sent">
          Report is still generating. Stay put, or <a href="#" @click.prevent="optInEmailWhenReady">get an in-app notification when it's done</a>.
        </template>
        <template v-else>
          Report is still generating. You'll get an in-app notification when it's done.
        </template>
      </span>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin:30px; font-weight: bold; font-size: 24px;">
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">Planned Time Diff&nbsp;&nbsp;<b-form-select v-model="duration_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">HOS Time Diff&nbsp;&nbsp;<b-form-select v-model="duration_hos_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt && summary.payroll_enabled" class="col" style="border: 1px solid grey;">Payroll Early Diff&nbsp;&nbsp;<b-form-select v-model="payroll_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt" class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;">Driver&nbsp;Tag&nbsp;<b-form-select v-model="selected_driver_tag" :options="driverTagOptions" style="font-size: 20px; font-weight: bold; width:250px"/></div>
        <div class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;">Filter&nbsp;<b-form-input style="width: 200px" v-model="booking_filter" placeholder="charterID,vehicle,driver"/></div>
        <div v-if="summary.total_cnt" class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;"><b-button size="md" variant="success" @click="download">Download Report</b-button></div>
      </div>
      <div class="row" style="margin:30px; font-weight: bold; font-size: 24px;">
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">{{items.length}} of {{summary.total_cnt}}</div>
        <div v-if="summary.hosHours" class="col" style="border: 1px solid grey;">Total HOS Hours: {{summary.hosHours}}</div>
        <div v-if="summary.payrollHours" class="col" style="border: 1px solid grey;">Total Payroll Hours: {{summary.payrollHours}}</div>
        <div v-if="summary.hosPercentUtilized" class="col" style="border: 1px solid grey;">{{summary.hosPercentUtilized}}% with HOS Logs</div>
        <div v-if="summary.payrollPercentUtilized" class="col" style="border: 1px solid grey;">{{summary.payrollPercentUtilized}}% with Payroll Records</div>
      </div>
    </div>

    <table v-if="!loading && items.length > 0" width="100%" border="1" cellpadding="2">
      <thead>
        <tr>
          <th colspan="6" style="font-size: 18px; font-weight: bold;">&nbsp;</th>
          <th colspan="3" style="background-color: #C6C6C6">Planned</th>
          <th colspan="3" style="background-color: #D5D5D5">HOS</th>
          <th v-if="summary.payroll_enabled" colspan="3" style="background-color: #E5E5E5">Payroll</th>
          <th colspan="2" style="background-color: #F5F5F5">Time Diffs</th>
          <th></th>
        </tr>
        <tr class="stripe">
          <th width="10px"></th>
          <th @click="sort_strings('driver')">Driver</th>
          <th @click="sort_strings('vehicle')">Vehicle</th>
          <th @click="sort_numbers('charterID')">CharterID</th>
          <th @click="sort_numbers('movementID')">MvmtID</th>
          <th @click="sort_strings('is_multiday')">Multiday</th>
          <th @click="sort_dates('plannedStartDateTime')">Start</th>
          <th @click="sort_dates('plannedFinishDateTime')">Finish</th>
          <th @click="sort_numbers('plannedMinutes')">Total</th>
          <th @click="sort_dates('actualHOSStartDateTime')">Start</th>
          <th @click="sort_dates('actualHOSFinishDateTime')">Finish</th>
          <th @click="sort_numbers('hosMinutes')">Total</th>
          <th v-if="summary.payroll_enabled" @click="sort_dates('actualPayrollStartDateTime')">Start</th>
          <th v-if="summary.payroll_enabled" @click="sort_dates('actualPayrollFinishDateTime')">Finish</th>
          <th v-if="summary.payroll_enabled" @click="sort_numbers('payrollMinutes')">Total</th>
          <th v-if="summary.payroll_enabled" @click="sort_numbers('plannedMinusPayroll')">Planned-Payroll Diff</th>
          <th v-else @click="sort_numbers('hosMinusPlanned')">HOS-Planned Diff</th>
          <th v-if="summary.payroll_enabled" @click="sort_numbers('hosMinusPayroll')">HOS-Payroll Diff</th>
          <th width="200px" @click="sort_strings('notes')">Payroll Notes</th>
          <th width="200px">Notes</th>
        </tr>
      </thead>
      <template v-for="item in items">
        <tr class="stripe">
          <td><b-button size="sm" @click="toggle_div(item,'isDetailsVisible')">{{ item.isDetailsVisible ? '-' : '+' }}</b-button></td>
          <td>{{ item.driver }}<a target="_blank" :href="item.driver_hos_link"><img style="margin: 0px 5px 5px 5px" src="/driver-icon.png"/></a></td>
          <td>{{ item.vehicle | trim(25) }}</td>
          <td>{{ item.charterID | trim(20) }}</td>
          <td>{{ item.movementID | trim(20)}}</td>
          <td>{{ item.is_multiday }}</td>
          <td>{{item.plannedStartDateTime | formatDateTimeSmall}}</td>
          <td>{{item.plannedFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{item.plannedMinutes | formatMinutes}}</td>
          <td>{{item.actualHOSStartDateTime | formatDateTimeSmall}}</td>
          <td>{{item.actualHOSFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{item.hosMinutes | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled" :class="{ 'late-duration': isPayrollEarly(item) }">{{item.actualPayrollStartDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{item.actualPayrollFinishDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{item.payrollMinutes | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled">{{item.plannedMinusPayroll | formatMinutes}}</td>
          <td v-else >{{item.hosMinusPlanned | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled">{{item.hosMinusPayroll | formatMinutes}}</td>
          <td align="left"><b-form-input style="border: 1px solid black; min-width:200px" v-model="item.event_notes" v-on:blur="save_notes(item)" @keydown.enter.native="save_notes(item)"/></td>
          <td>&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.booking_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">planned data</td>
          <td>{{ nested.vehicle | trim(25) }}</td>
          <td>{{ nested.charterID | trim(20) }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID | trim(20) }}</router-link></td>
          <td>&nbsp;</td>
          <td>{{nested.plannedStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.plannedFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.plannedMinutes | formatMinutes}}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.hos_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID }}</router-link></td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>{{nested.actualHOSStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.actualHOSFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.hosMinutes}}</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.payroll_results">
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID }}</router-link></td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">{{nested.actualPayrollStartDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{nested.actualPayrollFinishDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{nested.payrollMinutes | formatMinutes}}</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>{{ nested.notes }}</td>
          <td align="left">{{ formatPunchExceptions(nested.punch_exceptions) }}</td>
        </tr>
      </template>
    </table>
    <div v-if="loading && !polling">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>

</template>
<script>
import {api_url, get_actuals_driver_payroll_report, get_driver_list, register_email_when_ready, save_event_notes} from '../networking/apis'
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from "axios";

export default {
  name: 'actuals_driver_payroll_report',

  components: {
  },

  data() {
    return {
      booking_filter: '',
      items: [],
      items_full: [],
      summary: [],
      driver_options: [],
      driver_tags_options: [],
      selected_driver_tag: null,
      duration_threshold: null,
      duration_hos_threshold: null,
      payroll_threshold: null,
      early_threshold: 10,
      late_threshold: 10,
      duration_threshold_options: [
        { value: null, text: 'All' },
        { value: '5', text: '> 5m' },
        { value: '10', text: '> 10m' },
        { value: '15', text: '> 15m' },
        { value: '30', text: '> 30m' },
        { value: '45', text: '> 45m' },
        { value: '60', text: '> 1h' },
        { value: '120', text: '> 2h' },
      ],
      loading: false,
      results_cached_at: null,
      polling: false,
      email_opt_in_sent: false,
      details_fromDate: justDate(moment().subtract(1, 'days')),

      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },
  created() {
     this.check_features()
  },
  methods: {
    async check_features() {
      const {value: {data = []}} = await get_driver_list(this.user.tenant)
      this.driver_options = data
      this.driver_tags_options = this.getUniqueSortedTags(this.driver_options)
    },
    // FI-1432: user opts in to receive an email when this long-running daily report finishes.
    async optInEmailWhenReady() {
      try {
        await register_email_when_ready({
          tenant: this.user.tenant,
          report_type: 'daily_driver_report',
          params: {
            driver_tag: this.selected_driver_tag,
            date: this.details_fromDate,
          },
        })
        this.email_opt_in_sent = true
      } catch (e) {
        this.$emit('showAlert', "couldn't save the notification preference — try again", 'danger', 4000)
      }
    },
    getUniqueSortedTags(arr) {
      const allTags = arr.flatMap(item => {
        if (!item || !item.tags) return [];

        return item.tags.split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);
      });

      const uniqueTags = [...new Set(allTags)];
      uniqueTags.sort();
      return uniqueTags;
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    async load_data(reprocess) {
      if (this._retryTimerId) { clearTimeout(this._retryTimerId); this._retryTimerId = null }
      if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
      this.loading = true
      this.results_cached_at = null
      if (!this.polling) {
        this._pollingTimerId = setTimeout(() => { this._pollingTimerId = null; this.polling = true }, 30000)
      }
      try {
        const {value: {data = []}} = await get_actuals_driver_payroll_report(this.user.tenant, this.selected_driver_tag, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), reprocess, this.polling ? { timeout: 30000, poll: true } : {})
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        if (data.status === 'processing') {
          this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 10000)
          return
        }
        this.polling = false
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.results
        this.items_full = data.results
        this.results_cached_at = data.results_cached_at || null
        this.loading = false
        this.filter_items()
      } catch (e) {
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        this.polling = true
        this.loading = false
        this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 30000)
      }
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    async save_notes(item) {
      try {
        let json = {
          'event_hash': item.event_notes_hash,
          'event_target': item.event_target,
          'notes': item.event_notes
        }
        const {value: { data = [] } } = await save_event_notes(this.user.tenant, json)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    filter_items() {
      this.items = this.items_full
      if (this.selected_type)
        this.items = this.items.filter(item => item.booking_type == this.selected_type)
      if (this.booking_filter)
        this.items = this.items.filter(item => (this.contains(item.charterID,this.booking_filter) || this.contains(item.event_notes,this.booking_filter) || this.contains(item.driver,this.booking_filter) || this.contains(item.vehicle,this.booking_filter)))
      if (this.duration_threshold)
        this.items = this.items.filter(item => (item.hosMinusPlanned == null || isNaN(item.hosMinusPlanned) || Math.abs(item.hosMinusPlanned) > this.duration_threshold))
      if (this.duration_hos_threshold)
        this.items = this.items.filter(item => (item.hosMinusPayroll == null || isNaN(item.hosMinusPayroll) || Math.abs(item.hosMinusPayroll) > this.duration_threshold))
      if (this.selected_driver_tag !== null) {
        this.items = this.items.filter(item =>
          Array.isArray(item.tags) &&
          item.tags.some(tag => tag.name === this.selected_driver_tag)
        )
      }
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    sort_dates(col) {
      console.log("sort_dates:" + col)
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          // console.log("sort:" + a[col] + ", " + b[col])

          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return new Date(a[col]) - new Date(b[col])
          }
        })
      }
      this.sort_col = col
    },
    sort_numbers(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return a[col] - b[col]
          }
        })
      }
      this.sort_col = col
    },
    sort_strings(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            let fa = a[col].charAt(0).toLowerCase(),
                fb = b[col].charAt(0).toLowerCase();

            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          }
        })
      }
      this.sort_col = col
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info')
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/reports/actuals/driverpayroll/download`,
          {
            tenant: this.user.tenant,
            tag: this.selected_driver_tag,
            date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `payroll-daily-${this.details_fromDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
    isPayrollEarly(item) {
      if (!this.payroll_threshold || !item.actualPayrollStartDateTime || !item.plannedStartDateTime) return false;
      const diff = moment(item.plannedStartDateTime).diff(moment(item.actualPayrollStartDateTime), 'minutes');
      return diff > this.payroll_threshold;
    },
    formatPunchExceptions(punch_exceptions) {
      if (!punch_exceptions || !Array.isArray(punch_exceptions) || punch_exceptions.length === 0) return '';
      return punch_exceptions.join(' | ');
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    formattedCachedAt() {
      if (!this.results_cached_at) return ''
      const m = moment(this.results_cached_at)
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : this.results_cached_at
    },
    driverTagOptions() {
      const map = {};
      this.items_full.forEach(item =>
        (item.tags || []).forEach(tag => {
          map[tag.id] = tag.name;
        })
      );
      const opts = [{ value: null, text: 'All' }];
      Object.entries(map)
        .sort(([, aName], [, bName]) => aName.localeCompare(bName))
        .forEach(([id, name]) => {
          opts.push({ value: Number(id), text: name });
        });
      return opts;
    }
  },
  beforeDestroy() {
    if (this._retryTimerId) clearTimeout(this._retryTimerId)
    if (this._pollingTimerId) clearTimeout(this._pollingTimerId)
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    duration_threshold(val) {
      this.filter_items()
    },
    duration_hos_threshold(val) {
      this.filter_items()
    },
    booking_filter() {
      this.filter_items()
    },
    selected_driver_tag() {
      this.filter_items()
    }
  }
}
</script>

<style scoped>

thead {
  position: sticky;
  top: 0;
  background-color: #F9F8F8;
  border: 1px solid black;
}

tr:nth-child(even) {
  background-color: #f2f2f2 !important;
}

.arrows {font-size: 22px; font-weight: bold; color: var(--fi-rail-accent) !important }

.early-duration { background-color: hsla(120,100%,75%,0.2)}
.late-duration { background-color: rgba(255,0,0,0.2)}

.early { background-color: hsla(120,100%,75%,0.2)}
.late { background-color: rgba(255,0,0,0.2)}
.ontime { background-color: hsla(200,75%,50%,0.2)}
.private { background-color: hsla(200,40%,80%,0.2) }
.contract { background-color: hsla(250,60%,70%,0.2) }

</style>
